import { useLocalStore } from 'mobx-react-lite';

import Tasks from 'APP/Tasks';
import { ScheduleSettingsPopup } from 'APP/main/PopupManager/Popups/ScheduleSettingsPopup/ScheduleSettingsPopup';
import { StickersSettingsPopup } from 'APP/main/PopupManager/Popups/StickersSettingsPopup/StickersSettingsPopup';
import { ISticker, IStickerPackExtend } from 'APP/model/stickers/stickersModel.types';
import { storage } from 'APP/packages/storage';
import { Schedule } from 'APP/store/Groups/Schedule';
import Entities from 'STORE';
import { StickerPayload } from 'STORE/Messages/Message/Payload/StickerPayload/StickerPayload';
import { sortStickers } from 'UTILS/sortStickers';

interface IStickerPanelPresenter {
  activePackId: string | null;
  packs: IStickerPackExtend[];
  hasPacks: boolean;
  activePack: IStickerPackExtend | null;
  setActivePack(stickerPackId: string): void;
  sendSticker(sticker: ISticker): void;
  checkIsActivePack(stickerPackId: string): boolean;
  openSettings(): void;
}

export const useStickerPanelPresenter = (closePopover: () => void): IStickerPanelPresenter => {
  const presenter = useLocalStore<IStickerPanelPresenter>(() => ({
    activePackId: storage.selectedStickerPack.get(),

    get packs(): IStickerPackExtend[] {
      return [...Entities.stickersStore.installedPacks].sort(sortStickers);
    },

    get hasPacks(): boolean {
      return presenter.packs.length > 0;
    },

    get activePack(): IStickerPackExtend | null {
      return (
        presenter.packs.find((pack) => pack.stickerPack.id === presenter.activePackId) ||
        presenter.packs[0] ||
        null
      );
    },

    setActivePack(stickerPackId: string): void {
      presenter.activePackId = stickerPackId;
      storage.selectedStickerPack.set(stickerPackId);
    },

    checkIsActivePack(stickerPackId: string): boolean {
      return presenter.activePackId === stickerPackId;
    },

    sendSticker(sticker: ISticker): void {
      if (!sticker) {
        return;
      }

      const group = Entities.GroupsStore.activeGroup;
      if (!group) {
        return;
      }

      const messageData = {
        payload: new StickerPayload(sticker),
        repliedMessage: Entities.InputPanelsStore.activeInputPanel.replyMessage,
        quote: Entities.InputPanelsStore.activeInputPanel.quote,
      };

      const mustBeScheduled = group instanceof Schedule;
      const canSend = Tasks.messaging.canSendMessage({
        groupId: group.id,
        isCheckSlowMode: !mustBeScheduled,
        payload: messageData.payload,
      });

      if (!canSend.result) {
        canSend.error && Tasks.app.showToast(canSend.error);
        return;
      }

      if (mustBeScheduled) {
        Tasks.app.openPopup(ScheduleSettingsPopup, {
          params: {
            onSubmit: async (date: Date): Promise<void> => {
              await Tasks.messaging.createScheduledMessage(group.groupId!, {
                ...messageData,
                scheduleTime: date.getTime(),
              });
            },
          },
        });

        return;
      }

      Tasks.messaging.createNewMessages(group.id, [messageData]);
    },

    openSettings(): void {
      closePopover();

      Tasks.app.openPopup(StickersSettingsPopup);
    },
  }));

  return presenter;
};
